/* eslint-disable prettier/prettier */
'use client';

import * as React from 'react';
import { Button } from 'app/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'app/components/ui/tabs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable } from './data-table';
import { Badge } from 'app/components/ui/badge';
import { Package, Archive, FileText, CheckCircle, XCircle } from 'lucide-react';

export function ProductList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = React.useState(() => {
    return searchParams.get('status') || 'all';
  });
  const [tabCounts, setTabCounts] = React.useState({ all: 0, active: 0, inactive: 0, draft: 0, archived: 0 });

  // Get product type filter from URL query params
  const productTypeFilter = React.useMemo(() => {
    const productType = searchParams.get('product-type');
    const filter = productType ? { product_type: parseInt(productType) } : {};
    console.log('Product Type Filter:', filter, 'URL param:', productType);
    return filter;
  }, [searchParams]);

  const productType = (id: number) => {
    navigate(`/products/add?product_type=${id}`);
  };

  const getStatusFilter = (tab: string) => {
    switch (tab) {
      case 'draft':
        return { status: 1 };
      case 'active':
        return { status: 2 };
      case 'inactive':
        return { status: 3 };
      case 'archived':
        return { status: 4 };
      default:
        return {};
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    const newSearchParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newSearchParams.delete('status');
    } else {
      newSearchParams.set('status', value);
    }
    setSearchParams(newSearchParams);
  };

  return (
    <div className="w-full bg-white dark:bg-gray-900 px-4 mt-5">
      <div className="flex justify-between items-center gap-4 py-4">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="py-4">
          <TabsList className="bg-gray-50 dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700 shadow-none">
            <TabsTrigger
              value="all"
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=active]:shadow-sm transition-all duration-200 ease-in-out px-4 py-2 rounded-md flex items-center gap-2"
            >
              <Package className="w-4 h-4" />
              All
              <Badge variant="secondary" className="ml-1 text-xs">
                {tabCounts.all}
              </Badge>
            </TabsTrigger>
            <TabsTrigger
              value="active"
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=active]:shadow-sm transition-all duration-200 ease-in-out px-4 py-2 rounded-md flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
              Active
              <Badge variant="secondary" className="ml-1 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                {tabCounts.active}
              </Badge>
            </TabsTrigger>
            <TabsTrigger
              value="inactive"
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=active]:shadow-sm transition-all duration-200 ease-in-out px-4 py-2 rounded-md flex items-center gap-2"
            >
              <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
              Inactive
              <Badge variant="secondary" className="ml-1 text-xs bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                {tabCounts.inactive}
              </Badge>
            </TabsTrigger>
            <TabsTrigger
              value="draft"
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=active]:shadow-sm transition-all duration-200 ease-in-out px-4 py-2 rounded-md flex items-center gap-2"
            >
              <FileText className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              Draft
              <Badge variant="secondary" className="ml-1 text-xs bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
                {tabCounts.draft}
              </Badge>
            </TabsTrigger>
            <TabsTrigger
              value="archived"
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=active]:shadow-sm transition-all duration-200 ease-in-out px-4 py-2 rounded-md flex items-center gap-2"
            >
              <Archive className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              Archived
              <Badge variant="secondary" className="ml-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                {tabCounts.archived}
              </Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div>
          <Button onClick={() => navigate('/products/add')}>
            Add Product
          </Button>
        </div>
      </div>
      <DataTable
        statusFilter={getStatusFilter(activeTab)}
        productTypeFilter={productTypeFilter}
        activeTab={activeTab}
        onTabCountsUpdate={setTabCounts}
      />
    </div>
  );
}
